import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { RetentionSummary } from './types';
import { Eye, Users, Play, Target, Zap, MousePointerClick, DollarSign, Percent, TrendingUp as TrendingUpIcon } from 'lucide-react';
import { MetricCard } from '../common/MetricCard';
import { PitchKpiCard } from '../pitch/PitchKpiCard';
import type { VideoPitch } from '@/types/pitch';
import { PlatformKpi } from '../platform';
import { DateRange } from 'react-day-picker';
import { Platform, PlatformMetrics } from '@/types/platform';

interface RetentionSummaryTableProps {
  summary: RetentionSummary;
  isLoading: boolean;
  /** `video.pitches` do consolidado. */
  pitches?: VideoPitch[];
  /** Canetinha do KPI de pitch: id do pitch, ou null para adicionar. */
  onEditPitch?: (pitchId: string | null) => void;
  /** "Detalhes" do KPI de pitch. */
  onPitchDetails?: (pitchId: string) => void;
  videoId?: string | null;
  dateRange?: DateRange;
  conversionsPlatforms?: Array<{
    name: string;
    logo: string;
    conversions: number;
    revenue: number;
  }>;
}

export function RetentionSummaryTable({
  summary,
  isLoading,
  pitches = [],
  onEditPitch,
  onPitchDetails,
  videoId,
  dateRange,
  conversionsPlatforms = []
}: RetentionSummaryTableProps) {
  useLanguage();
  // Transformar dados de conversões por plataforma para o formato esperado
  const platforms = useMemo<Platform[]>(() => {
    return conversionsPlatforms.map((platform, index) => ({
      id: platform.name.toLowerCase().replace(/\s+/g, '-'),
      name: platform.name,
      logo_url: platform.logo || '',
      status: 'active' as const,
      connected_at: new Date().toISOString(),
      primary: index === 0
    }));
  }, [conversionsPlatforms]);

  const platformMetrics = useMemo<PlatformMetrics[]>(() => {
    return conversionsPlatforms.map(platform => ({
      platform_id: platform.name.toLowerCase().replace(/\s+/g, '-'),
      conversions: platform.conversions,
      revenue: {
        amount: platform.revenue,
        currency: summary?.currency || 'BRL'
      },
      average_ticket: platform.conversions > 0 ? platform.revenue / platform.conversions : 0
    }));
  }, [conversionsPlatforms, summary?.currency]);

  // Verificação defensiva para summary undefined
  if (!summary || !isLoading && (!summary.views_total && summary.views_total !== 0)) {
    return (
      <div className="space-y-[14px] md:space-y-[18px]">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 md:gap-[18px]">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-[70px] md:h-[110px] bg-[#F3F6FF] animate-pulse rounded-[16px] md:rounded-[14px]" />
          ))}
        </div>
      </div>
    );
  }

  const performanceMetrics = [
    {
      label: translateInterfaceText("Visualizações"),
      value: (summary.views_total || 0).toLocaleString(getInterfaceLanguage()),
      color: 'text-[#374151]',
      description: translateInterfaceText("Número total de vezes que o vídeo foi carregado, independente se foi assistido ou não."),
      icon: Eye,
    },
    {
      label: translateInterfaceText("Vis. Únicas"),
      value: (summary.views_unique || 0).toLocaleString(getInterfaceLanguage()),
      color: 'text-[#374151]',
      description: translateInterfaceText("Quantidade de pessoas diferentes que visualizaram o vídeo."),
      icon: Users,
    },
    {
      label: translateInterfaceText("Plays"),
      value: (summary.plays_total || 0).toLocaleString(getInterfaceLanguage()),
      color: 'text-[#374151]',
      description: translateInterfaceText("Número total de vezes que o play foi dado no vídeo."),
      icon: Play,
    },
    {
      label: translateInterfaceText("Plays Únicos"),
      value: (summary.plays_unique || 0).toLocaleString(getInterfaceLanguage()),
      color: 'text-[#374151]',
      description: translateInterfaceText("Quantidade de pessoas diferentes que deram play no vídeo."),
      icon: Target,
    },
    {
      label: translateInterfaceText("Play Rate"),
      value: `${((summary.play_rate || 0) * 100).toFixed(1)}%`,
      color: 'text-[#374151]',
      description: translateInterfaceText("Das {value0} pessoas que viram seu vídeo, {value1} deram play.", { value0: ((summary.views_unique || 0).toLocaleString(getInterfaceLanguage())), value1: ((summary.plays_unique || 0).toLocaleString(getInterfaceLanguage())) }),
      icon: Zap,
    },
    {
      label: translateInterfaceText("Engajamento"),
      value: `${((summary.engagement || 0) * 100).toFixed(1)}%`,
      color: 'text-[#374151]',
      description: translateInterfaceText("Percentual médio do vídeo que foi assistido pelos espectadores."),
      icon: MousePointerClick,
    },
  ];

  const monetizationMetrics = [
    {
      label: translateInterfaceText("Cliques"),
      value: (summary.cta_clicks || 0).toLocaleString(getInterfaceLanguage()),
      color: 'text-[#374151]',
      description: translateInterfaceText("Número total de cliques no botão de call-to-action (CTA) do vídeo."),
      icon: MousePointerClick,
    },
    {
      label: translateInterfaceText("Conversões"),
      value: ((summary as any).conversions || 0).toLocaleString(getInterfaceLanguage()),
      color: 'text-[#374151]',
      description: translateInterfaceText("Quantidade de conversões registradas a partir deste vídeo."),
      icon: TrendingUpIcon,
    },
    {
      label: translateInterfaceText("Taxa Conv."),
      value: `${(((summary as any).conversion_rate || 0) * 100).toFixed(1)}%`,
      color: 'text-[#374151]',
      tooltip: translateInterfaceText("Conversões ÷ Plays únicos no período"),
      description: translateInterfaceText("Das {value0} pessoas que deram play, {value1} converteram.", { value0: ((summary.plays_unique || 0).toLocaleString(getInterfaceLanguage())), value1: (((summary as any).conversions || 0).toLocaleString(getInterfaceLanguage())) }),
      icon: Percent,
    },
    {
      label: translateInterfaceText("Receita"),
      value: new Intl.NumberFormat(getInterfaceLanguage(), {
        style: 'currency',
        currency: (summary as any).currency || 'BRL',
      }).format((summary as any).revenue || 0),
      color: 'text-[#374151]',
      description: translateInterfaceText("Receita total gerada a partir de conversões deste vídeo."),
      icon: DollarSign,
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-[14px] md:space-y-[18px]">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 md:gap-[18px]">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-[70px] md:h-[110px] bg-[#F3F6FF] animate-pulse rounded-[16px] md:rounded-[14px]" />
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 md:gap-[18px]">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-[70px] md:h-[110px] bg-[#F3F6FF] animate-pulse rounded-[16px] md:rounded-[14px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-[14px] md:space-y-[18px]">
      {/* Linha 1: Desempenho do Player - Mobile: 2 colunas, Desktop: 6 */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 md:gap-[18px]">
        {performanceMetrics.map((metric) => (
          <MetricCard
            key={metric.label}
            className="metric-card-premium"
            label={metric.label}
            value={metric.value}
            color={metric.color}
            description={metric.description}
            icon={metric.icon}
          />
        ))}
      </div>

      {/* Linha 2: Conversão e Monetização - Mobile: 2 colunas, Desktop: 7 (o KPI de pitch ocupa 2) */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 md:gap-[18px]">
        <PitchKpiCard
          className="col-span-2"
          pitches={pitches}
          metrics={summary.pitches ?? []}
          onEdit={(pitchId) => onEditPitch?.(pitchId)}
          onDetails={(pitchId) => onPitchDetails?.(pitchId)}
        />
        {monetizationMetrics.map((metric) => (
          <MetricCard
            key={metric.label}
            className="metric-card-premium"
            label={metric.label}
            value={metric.value}
            color={metric.color}
            tooltip={(metric as any).tooltip}
            description={metric.description}
            icon={metric.icon}
          />
        ))}
        
        {/* KPI Plataforma */}
        <PlatformKpi
          platforms={platforms}
          metrics={platformMetrics}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
